/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { InitiativePlain, RootInitiativeData } from '../types/initiative';
import { PERMISSION_GROUPS, getPermissionGroupConfiguration } from '@utils/permission-groups';
import { AppConfig } from '../types/app';
import { getInitiativeTree, getInitiativeTreeData, getInitiativeTypeTree } from './initiativeTree';
import { findAllChildrenById } from '@utils/initiative';
import { CurrentUserState } from '../reducers/current-user';
import { GlobalDataState } from '../reducers/global-data';
import { InitiativeState } from '../reducers/initiative';
import { GenericState } from '../slice/slice';

interface State {
  initiative: InitiativeState;
  rootInitiatives: GenericState<RootInitiativeData[]>;
  currentUser: CurrentUserState;
  globalData: GlobalDataState;
}

export const currentInitiative = (state: State) => state.initiative.loaded ? state.initiative.data : undefined;
const rootInitiatives = (state: State) => state.rootInitiatives.loaded ? state.rootInitiatives.data : undefined;
const userInitiatives = (state: State) => state.currentUser.loaded ? state.currentUser.data.initiativeTree : undefined;
export const getRootOrg = (state: State) => state.globalData.loaded ? state.globalData.organization : undefined;
export const getRootAppConfig = (state: State): AppConfig | undefined => {
  return state.globalData.loaded ? state.globalData.data.appConfig : undefined;
};

const getGlobalData = (state: State) => state.globalData.loaded ? state.globalData.data : undefined;

export const getCompanyAgreements = createSelector([getGlobalData], (globalData) => {
  return globalData?.companyAgreementsRequired ?? [];
});

export const getLevelUserRoles = createSelector([currentInitiative], (initiative) => {
  if (initiative && initiative.userPermissions) {
    return initiative.userPermissions.permissions;
  }
  return [];
});

export const getInitiativeUsage = createSelector([currentInitiative], (initiative) => {
  return initiative ? initiative.usage : [];
});

export const hasSubscriptionSetup = createSelector([currentInitiative], (initiative) => {
  // Has customer and it is not WWG one
  return Boolean(initiative?.customer) && initiative?.customer?.id !== 'wwg';
});

export const getUserPermissionGroupIds = createSelector([userInitiatives, rootInitiatives], (initiativeTree, rootInitiativeTree) => {
  const defaultPermissionGroup = PERMISSION_GROUPS.FREE;
  const distinctPermissionGroups = new Set([defaultPermissionGroup]);
  if (Array.isArray(initiativeTree)) {
    initiativeTree
      .filter(i => i.permissionGroup && i.permissionGroup !== defaultPermissionGroup)
      .forEach(i => {
        if (i.permissionGroup) {
          distinctPermissionGroups.add(i.permissionGroup)
        }
      });
  }

  // Add root level that will solve child level (FREE permission) access only
  if (Array.isArray(rootInitiativeTree)) {
    rootInitiativeTree.forEach(i => {
      if (i.permissionGroup && i.permissionGroup !== defaultPermissionGroup) {
        distinctPermissionGroups.add(i.permissionGroup)
      }
    });
  }

  return Array.from(distinctPermissionGroups);
});

export const getUserPermissionGroups = createSelector([getUserPermissionGroupIds], (permissionGroupIds) => {
  return permissionGroupIds.map(id => getPermissionGroupConfiguration(id));
});

export const isSingleOrg = createSelector([getRootOrg, getInitiativeTree], (rootInitiative, initiativeTree) => {
  const parentNode = initiativeTree.children.find((company) => company.id === rootInitiative?._id);
  return parentNode && parentNode.children?.length === 0;
});

export const isRootOrg = createSelector([getRootOrg, currentInitiative], (rootInitiative, initiative) => {
  return rootInitiative?._id === initiative?._id;
});

export const checkIsBottomOfInitiativeTree = createSelector(
  [getInitiativeTreeData, currentInitiative],
  (initiativeTree, initiative) =>
    Boolean(initiativeTree && initiative?._id && !initiativeTree.some((node) => node.parentId === initiative._id))
);

/**
 * Get the current initiative and all of its children with level information
 */
export const getCurrentInitiativeTree = createSelector(
  [currentInitiative, getInitiativeTypeTree],
  (current, initiativeTree): Array<InitiativePlain & { level: number }> =>
    current ? [{ ...current, level: 0 }, ...findAllChildrenById(initiativeTree, current._id)] : []
);
